import React from 'react';

import { useIntl } from 'react-intl-next';

import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	usePrimaryProduct,
} from '../../../../../controllers/store';
import { filter as positionStatusFilter } from '../../../../../controllers/store/search/filters/position-status';

import { i18n } from './messages';

const PositionStatusFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
}: FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({ filterId: positionStatusFilter.id });

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.positionStatusFilterLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.positionStatusFilterLabelUnselected),
			options: suggestedOptions,
		},
	];

	const label =
		fit === 'compact' && selectedOptions.length > 0
			? ''
			: formatMessage(i18n.positionStatusFilterLabel);

	return (
		<SelectFilter
			canClearFilter
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.positionStatusFilterPlaceholder)}
			filterOrControlId={'positionStatus'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			onClearOptions={clearOptions}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default PositionStatusFilter;
